/**
 * Sesi login idle 24 jam — aktivitas merefresh timer; habis → logout otomatis.
 */
export const IDLE_SESSION_MS = 24 * 60 * 60 * 1000;
const AUTH_KEY = 'jgi_auth';
const TOUCH_THROTTLE_MS = 15_000;
const SERVER_TOUCH_MS = 10 * 60 * 1000; // refresh cookie tiap 10 menit aktivitas
let lastServerTouch = 0;

type AuthBlob = {
  user?: string;
  at?: number;
  lastActive?: number;
  remember?: boolean;
  [k: string]: unknown;
};

function readAuth(): AuthBlob | null {
  if (!import.meta.client) return null;
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AuthBlob;
  } catch {
    return null;
  }
}

function writeAuth(patch: Partial<AuthBlob>) {
  if (!import.meta.client) return;
  try {
    const prev = readAuth() || {};
    localStorage.setItem(AUTH_KEY, JSON.stringify({ ...prev, ...patch }));
  } catch {
    /* ignore */
  }
}

export function getLastActive(): number | null {
  const a = readAuth();
  if (!a) return null;
  const n = Number(a.lastActive || a.at || 0);
  return n > 0 ? n : null;
}

/** Saat login sukses — mulai hitung idle dari sekarang. */
export function startIdleSession() {
  const now = Date.now();
  writeAuth({ at: now, lastActive: now });
}

export function touchIdleSession(force = false) {
  if (!import.meta.client) return;
  if (!readAuth()) return;
  const now = Date.now();
  const last = getLastActive() || 0;
  if (!force && now - last < TOUCH_THROTTLE_MS) return;
  writeAuth({ lastActive: now });
  if (force || now - lastServerTouch >= SERVER_TOUCH_MS) {
    lastServerTouch = now;
    void $fetch('/api/auth/touch', { method: 'POST' }).catch(() => {
      /* ignore */
    });
  }
}

export function remainingIdleMs(now = Date.now()): number {
  const last = getLastActive();
  if (!last) return 0;
  return Math.max(0, IDLE_SESSION_MS - (now - last));
}

export function isIdleExpired(now = Date.now()): boolean {
  const last = getLastActive();
  if (!last) return true;
  return now - last >= IDLE_SESSION_MS;
}

/** Format sisa waktu HH:MM:SS */
export function formatCountdown(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/** Jumat - 08/08/2026, 11:12 AM */
export function formatSessionClock(d: Date, locale: 'id' | 'en' = 'id'): string {
  const daysId = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
  const daysEn = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const day = (locale === 'en' ? daysEn : daysId)[d.getDay()] || '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  let h = d.getHours();
  const min = String(d.getMinutes()).padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  const hh = String(h).padStart(2, '0');
  return `${day} - ${dd}/${mm}/${yyyy}, ${hh}:${min} ${ampm}`;
}

export function useIdleSession() {
  const { t, locale } = useI18n();
  const router = useRouter();
  const nowTick = useState<number>('jgi-idle-now', () => Date.now());
  const remainingMs = computed(() => remainingIdleMs(nowTick.value));
  const countdown = computed(() => formatCountdown(remainingMs.value));
  const clockLabel = computed(() =>
    formatSessionClock(new Date(nowTick.value), locale.value === 'en' ? 'en' : 'id')
  );
  const sessionLabel = computed(() =>
    t('auth.sessionCountdown', { time: countdown.value })
  );
  const urgent = computed(() => remainingMs.value > 0 && remainingMs.value < 15 * 60 * 1000);

  let tickTimer: ReturnType<typeof setInterval> | undefined;
  let loggingOut = false;

  async function forceLogout(reason: 'idle' | 'manual' = 'idle') {
    if (loggingOut || !import.meta.client) return;
    loggingOut = true;
    try {
      await $fetch('/api/auth/logout', { method: 'POST' });
    } catch {
      /* ignore */
    }
    try {
      localStorage.removeItem(AUTH_KEY);
    } catch {
      /* ignore */
    }
    window.dispatchEvent(new Event('jgi-auth-changed'));
    const q = reason === 'idle' ? '?reason=idle' : '';
    await router.push(`/auth/login${q}`);
    loggingOut = false;
  }

  function onActivity() {
    touchIdleSession();
    nowTick.value = Date.now();
  }

  function startWatchers() {
    if (!import.meta.client) return;
    const started = useState<boolean>('jgi-idle-watchers', () => false);
    if (started.value) return;
    started.value = true;

    // Migrasi: auth lama tanpa lastActive → anggap aktif sekarang (jangan kick langsung)
    const a = readAuth();
    if (a && !a.lastActive) {
      writeAuth({ lastActive: Date.now(), at: a.at || Date.now() });
    } else if (a && !getLastActive()) {
      writeAuth({ lastActive: Date.now() });
    }

    const bump = () => onActivity();
    const evts: Array<keyof WindowEventMap> = [
      'mousemove',
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
      'click',
      'visibilitychange',
    ];
    for (const e of evts) {
      window.addEventListener(e, bump, { passive: true });
    }

    tickTimer = setInterval(() => {
      nowTick.value = Date.now();
      if (!readAuth()) return;
      if (isIdleExpired(nowTick.value)) {
        void forceLogout('idle');
      }
    }, 1000);
  }

  return {
    nowTick,
    remainingMs,
    countdown,
    clockLabel,
    sessionLabel,
    urgent,
    startWatchers,
    forceLogout,
    touchIdleSession,
    startIdleSession,
  };
}
